import React, { useState } from 'react';
import { 
  Card, 
  Form, 
  Input, 
  Button, 
  message, 
  Row, 
  Col, 
  Typography,
  Avatar,
  Divider
} from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from 'react-query';
import { usersAPI } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';

const { Title, Text } = Typography;

const Profile = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  const [profileForm] = Form.useForm();
  const [passwordForm] = Form.useForm();

  const updateProfileMutation = useMutation(usersAPI.updateProfile, {
    onSuccess: () => {
      message.success('Profil mis à jour avec succès');
      queryClient.invalidateQueries('user');
    },
    onError: (error) => {
      message.error(error.response?.data?.message || 'Erreur lors de la mise à jour');
    }
  });

  const changePasswordMutation = useMutation(usersAPI.changePassword, {
    onSuccess: () => {
      message.success('Mot de passe changé avec succès');
      passwordForm.resetFields();
    },
    onError: (error) => {
      message.error(error.response?.data?.message || 'Erreur lors du changement');
    }
  });

  const handleProfileSubmit = (values) => {
    updateProfileMutation.mutate(values);
  };

  const handlePasswordSubmit = (values) => {
    changePasswordMutation.mutate(values);
  };

  return (
    <div>
      <div className="page-header">
        <Title level={2}>Mon Profil</Title>
      </div>

      <Row gutter={[24, 24]}>
        <Col xs={24} lg={8}>
          <Card style={{ textAlign: 'center' }}>
            <Avatar 
              size={100} 
              icon={<UserOutlined />} 
              src={user?.avatar}
              style={{ marginBottom: 16 }}
            />
            <Title level={4}>{user?.first_name} {user?.last_name}</Title>
            <Text type="secondary">{user?.email}</Text>
            <Divider />
            <div>
              <Text strong>Rôle: </Text>
              <Text>{user?.role === 'admin' ? 'Administrateur' : 'Utilisateur'}</Text>
            </div>
            <div style={{ marginTop: 8 }}>
              <Text strong>Statut: </Text>
              <Text style={{ color: user?.is_active ? '#52c41a' : '#ff4d4f' }}>
                {user?.is_active ? 'Actif' : 'Inactif'}
              </Text>
            </div>
          </Card>
        </Col>

        <Col xs={24} lg={16}>
          <Card title="Informations personnelles" style={{ marginBottom: 24 }}>
            <Form
              form={profileForm}
              layout="vertical"
              onFinish={handleProfileSubmit}
              initialValues={{
                first_name: user?.first_name,
                last_name: user?.last_name,
                email: user?.email,
                phone: user?.phone
              }}
            >
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="first_name"
                    label="Prénom"
                    rules={[{ required: true, message: 'Veuillez saisir votre prénom' }]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="last_name"
                    label="Nom"
                    rules={[{ required: true, message: 'Veuillez saisir votre nom' }]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: true, message: 'Veuillez saisir votre email' },
                  { type: 'email', message: 'Veuillez saisir un email valide' }
                ]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                name="phone"
                label="Téléphone"
              >
                <Input />
              </Form.Item>

              <Form.Item>
                <Button 
                  type="primary" 
                  htmlType="submit"
                  loading={updateProfileMutation.isLoading}
                >
                  Mettre à jour le profil
                </Button>
              </Form.Item>
            </Form>
          </Card>

          <Card title="Changer le mot de passe">
            <Form
              form={passwordForm}
              layout="vertical"
              onFinish={handlePasswordSubmit}
            >
              <Form.Item
                name="current_password"
                label="Mot de passe actuel"
                rules={[{ required: true, message: 'Veuillez saisir votre mot de passe actuel' }]}
              >
                <Input.Password prefix={<LockOutlined />} />
              </Form.Item>

              <Form.Item
                name="new_password"
                label="Nouveau mot de passe"
                rules={[
                  { required: true, message: 'Veuillez saisir votre nouveau mot de passe' },
                  { min: 6, message: 'Le mot de passe doit contenir au moins 6 caractères' }
                ]}
              >
                <Input.Password prefix={<LockOutlined />} />
              </Form.Item>

              <Form.Item
                name="confirm_password"
                label="Confirmer le nouveau mot de passe"
                dependencies={['new_password']}
                rules={[
                  { required: true, message: 'Veuillez confirmer votre nouveau mot de passe' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('new_password') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('Les mots de passe ne correspondent pas'));
                    },
                  }),
                ]}
              >
                <Input.Password prefix={<LockOutlined />} />
              </Form.Item>

              <Form.Item>
                <Button 
                  type="primary" 
                  htmlType="submit"
                  loading={changePasswordMutation.isLoading}
                >
                  Changer le mot de passe
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Profile;
